// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/CommunityNavigatorLayout.tsx

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
} from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./CommunityNavigatorLayout.css";
import {
  IntakeUnsavedLeaveGuardProvider,
  useTryNavigateAwayWithIntakeGuard,
} from "./intakeUnsavedLeaveGuard";
import { portalFetch } from "../../../utils/portalRoleChoice";

type NavItem = {
  label: string;
  to: string;
  end?: boolean;
};

type BootstrapRole = {
  roleName?: string;
  ohtId?: number | string | null;
  ohtName?: string | null;
  ohtCode?: string | null;
  isActive?: boolean | number | string | null;
};

type NavigatorOhtScope = {
  ohtName: string;
  ohtCode: string;
};

const COMPACT_NAV_QUERY = "(max-width: 860px)";
const SIDEBAR_COLLAPSED_KEY = "communityNavigatorSidebarCollapsed";

const DRAWER_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

const navItems: NavItem[] = [
  {
    label: "Dashboard",
    to: "/portal/community-navigator",
    end: true,
  },
  {
    label: "Cases",
    to: "/portal/community-navigator/cases",
  },
  {
    label: "Uploads",
    to: "/portal/community-navigator/uploads",
  },
  {
    label: "Reports",
    to: "/portal/community-navigator/reports",
  },
];

const SIDEBAR_INTAKE_COUNT_PATH =
  "/api/portal/community-navigator/cases?sidebarSummary=1";
const SIDEBAR_INTAKE_COUNT_INTERVAL_MS = 60_000;

function formatIntakeNotStartedBadge(count: number): string | null {
  if (!Number.isFinite(count) || count <= 0) return null;
  if (count > 99) return "99+";
  return String(Math.trunc(count));
}

function isRoleActive(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  return String(value).trim().toLowerCase() === "true";
}

function cleanScopeValue(value: unknown): string {
  const text = String(value ?? "").trim();
  const normalized = text.toLowerCase();
  if (!text || normalized === "undefined" || normalized === "null") return "";
  return text;
}

function findActiveCommunityNavigatorRole(
  roles: BootstrapRole[]
): BootstrapRole | undefined {
  return roles.find(
    (role) =>
      isRoleActive(role.isActive) &&
      String(role.roleName || "").trim().toLowerCase() === "communitynavigator"
  );
}

function readNavigatorOhtScope(
  role: BootstrapRole | undefined
): NavigatorOhtScope | null {
  if (!role) return null;

  const ohtName = cleanScopeValue(role.ohtName);
  const ohtCode = cleanScopeValue(role.ohtCode);
  if (!ohtName && !ohtCode) return null;

  return { ohtName, ohtCode };
}

function formatNavigatorOhtScope(scope: NavigatorOhtScope | null): string | null {
  if (!scope) return null;
  if (scope.ohtName && scope.ohtCode) return `${scope.ohtName} · ${scope.ohtCode}`;
  return scope.ohtName || scope.ohtCode || null;
}

function CommunityNavigatorNavIcon({ label }: { label: string }) {
  const props = {
    className: "community-navigator-nav-icon",
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
    focusable: "false" as const,
  };

  if (label === "Dashboard") {
    return (
      <svg {...props}>
        <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="3.5" width="7" height="7" rx="1.5" />
        <rect x="3.5" y="13.5" width="7" height="7" rx="1.5" />
        <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
      </svg>
    );
  }

  if (label === "Cases") {
    return (
      <svg {...props}>
        <rect x="8" y="3" width="8" height="4" rx="1" />
        <path d="M8 5H6.5A2.5 2.5 0 0 0 4 7.5v11A2.5 2.5 0 0 0 6.5 21h11a2.5 2.5 0 0 0 2.5-2.5v-11A2.5 2.5 0 0 0 17.5 5H16" />
        <path d="M9 12h6" />
        <path d="M9 16h6" />
      </svg>
    );
  }

  if (label === "Uploads") {
    return (
      <svg {...props}>
        <path d="M12 16V5" />
        <path d="M7.5 9.5 12 5l4.5 4.5" />
        <path d="M5 19h14" />
      </svg>
    );
  }

  if (label === "Reports") {
    return (
      <svg {...props}>
        <path d="M4 19h16" />
        <path d="M7 19V11" />
        <path d="M12 19V7" />
        <path d="M17 19V13" />
      </svg>
    );
  }

  return null;
}

function MenuIcon() {
  return (
    <svg
      className="community-navigator-menu-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function FooterActionIcon({ name }: { name: "public" | "role" | "signOut" }) {
  const props = {
    className: "community-navigator-footer-icon",
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true as const,
    focusable: "false" as const,
  };

  if (name === "public") {
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="8" />
        <path d="M4 12h16" />
        <path d="M12 4a12 12 0 0 1 0 16" />
        <path d="M12 4a12 12 0 0 0 0 16" />
      </svg>
    );
  }

  if (name === "role") {
    return (
      <svg {...props}>
        <path d="M7 8h11" />
        <path d="M15 5l3 3-3 3" />
        <path d="M17 16H6" />
        <path d="M9 13l-3 3 3 3" />
      </svg>
    );
  }

  return (
    <svg {...props}>
      <path d="M10 6H6.5A1.5 1.5 0 0 0 5 7.5v9A1.5 1.5 0 0 0 6.5 18H10" />
      <path d="M13 12h7" />
      <path d="m16.5 8.5 3.5 3.5-3.5 3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      className="community-navigator-drawer-close-icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

function compactNavQueryMatches() {
  return window.matchMedia(COMPACT_NAV_QUERY).matches;
}

function readSidebarCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarCollapsedPreference(collapsed: boolean) {
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    // Preference is optional and must not block navigation.
  }
}

function setElementInert(element: HTMLElement | null, inert: boolean) {
  if (!element) return;
  (element as HTMLElement & { inert: boolean }).inert = inert;
}

function getDrawerFocusable(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE_SELECTOR)
  ).filter((element) => {
    if (element.closest("[inert]")) return false;
    return element.getClientRects().length > 0;
  });
}

function extractEmailFromAuthMePayload(payload: any): string | null {
  const principal = Array.isArray(payload?.clientPrincipal)
    ? payload.clientPrincipal[0]
    : payload?.clientPrincipal;

  const userDetails = String(principal?.userDetails || "").trim();
  if (userDetails.includes("@")) return userDetails;

  const claims = Array.isArray(principal?.claims) ? principal.claims : [];
  const emailClaim = claims.find((claim: any) => {
    const type = String(claim?.typ || "").toLowerCase();
    return (
      type === "emails" ||
      type === "email" ||
      type === "preferred_username" ||
      type ===
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
    );
  });

  const email = String(emailClaim?.val || "").trim();
  return email.includes("@") ? email : null;
}

export default function CommunityNavigatorLayout() {
  return (
    <IntakeUnsavedLeaveGuardProvider>
      <CommunityNavigatorLayoutShell />
    </IntakeUnsavedLeaveGuardProvider>
  );
}

function CommunityNavigatorLayoutShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const tryNavigateAway = useTryNavigateAwayWithIntakeGuard();
  const sidebarRef = useRef<HTMLElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [hasMultipleRoles, setHasMultipleRoles] = useState(false);
  const [ohtScope, setOhtScope] = useState<NavigatorOhtScope | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isCompactNav, setIsCompactNav] = useState(compactNavQueryMatches);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    readSidebarCollapsedPreference
  );
  const [intakeNotStartedCount, setIntakeNotStartedCount] = useState<number | null>(
    null
  );
  const ohtScopeLabel = formatNavigatorOhtScope(ohtScope);
  const drawerIsModal = isCompactNav && drawerOpen;
  const desktopCollapsed = sidebarCollapsed && !isCompactNav;
  const casesIntakeBadge = formatIntakeNotStartedBadge(
    intakeNotStartedCount ?? 0
  );

  function closeDrawer() {
    setDrawerOpen(false);
  }

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((current) => {
      const next = !current;
      writeSidebarCollapsedPreference(next);
      return next;
    });
  }

  function dismissDrawer() {
    menuButtonRef.current?.focus();
    setDrawerOpen(false);
  }

  function handleGuardedNavClick(
    event: MouseEvent<HTMLAnchorElement>,
    to: string
  ) {
    event.preventDefault();
    closeDrawer();
    tryNavigateAway(() => {
      navigate(to);
    });
  }

  const signOutHref = useMemo(() => {
    const postLogout = encodeURIComponent(`${window.location.origin}/`);
    return `/.auth/logout?post_logout_redirect_uri=${postLogout}`;
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadSignedInUser() {
      try {
        const response = await fetch("/.auth/me", {
          credentials: "include",
        });

        if (!response.ok) return;

        const data = await response.json();
        if (!mounted) return;

        setSignedInEmail(extractEmailFromAuthMePayload(data));
      } catch {
        // Silent: signed-in email is an optional sidebar enhancement.
      }
    }

    loadSignedInUser();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadRoleContext() {
      try {
        const response = await fetch("/api/portal-auth-bootstrap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) return;

        const data = await response.json();
        if (!mounted) return;

        const roles: BootstrapRole[] = Array.isArray(data?.roles)
          ? data.roles
          : [];

        const activeRoleCount = roles.filter((role) =>
          isRoleActive(role.isActive)
        ).length;

        setHasMultipleRoles(activeRoleCount > 1);
        setOhtScope(
          readNavigatorOhtScope(findActiveCommunityNavigatorRole(roles))
        );
      } catch {
        // Silent: Switch Role stays hidden if role context cannot be loaded.
      }
    }

    loadRoleContext();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadIntakeNotStartedCount() {
      try {
        const response = await portalFetch(SIDEBAR_INTAKE_COUNT_PATH, {
          method: "GET",
          credentials: "include",
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!mounted) return;

        const count = Number(data?.intakeNotStarted);
        if (!Number.isFinite(count) || count < 0) return;
        setIntakeNotStartedCount(Math.trunc(count));
      } catch {
        // Keep the last safe badge value; do not disrupt portal navigation.
      }
    }

    loadIntakeNotStartedCount();

    const intervalId = window.setInterval(
      loadIntakeNotStartedCount,
      SIDEBAR_INTAKE_COUNT_INTERVAL_MS
    );

    function onWindowFocus() {
      void loadIntakeNotStartedCount();
    }

    window.addEventListener("focus", onWindowFocus);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onWindowFocus);
    };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    const media = window.matchMedia(COMPACT_NAV_QUERY);

    function applyCompactNav() {
      const compact = media.matches;
      setIsCompactNav(compact);
      if (!compact) setDrawerOpen(false);
    }

    applyCompactNav();
    media.addEventListener("change", applyCompactNav);
    return () => media.removeEventListener("change", applyCompactNav);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useLayoutEffect(() => {
    const sidebar = sidebarRef.current;
    const main = mainRef.current;
    const sidebarShouldBeInert = isCompactNav && !drawerOpen;

    if (
      sidebarShouldBeInert &&
      sidebar &&
      document.activeElement instanceof HTMLElement &&
      sidebar.contains(document.activeElement)
    ) {
      document.activeElement.blur();
    }

    setElementInert(sidebar, sidebarShouldBeInert);
    setElementInert(main, isCompactNav && drawerOpen);

    if (!drawerOpen || !isCompactNav) return;
    closeButtonRef.current?.focus();
  }, [drawerOpen, isCompactNav]);

  useLayoutEffect(() => {
    if (!drawerOpen || !isCompactNav) return;

    const scrollY = window.scrollY;
    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      window.scrollTo({ top: scrollY, left: 0, behavior: "auto" });
    };
  }, [drawerOpen, isCompactNav]);

  useEffect(() => {
    if (!drawerIsModal) return;

    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        menuButtonRef.current?.focus();
        setDrawerOpen(false);
        return;
      }

      if (event.key !== "Tab" || !sidebar) return;

      const focusable = getDrawerFocusable(sidebar);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !sidebar.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last || !sidebar.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerIsModal]);

  return (
    <div className="community-navigator-shell">
      <div className="community-navigator-mobile-header">
        <img
          src="/logo-1.png"
          alt=""
          className="community-navigator-mobile-logo"
        />
        <div className="community-navigator-mobile-title">Community Navigator</div>
        <button
          type="button"
          ref={menuButtonRef}
          className="community-navigator-menu-button"
          aria-expanded={drawerOpen}
          aria-controls="community-navigator-sidebar"
          aria-label={
            drawerOpen
              ? "Close Community Navigator menu"
              : "Open Community Navigator menu"
          }
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <MenuIcon />
        </button>
      </div>

      {isCompactNav ? (
        <div
          className={
            drawerOpen
              ? "community-navigator-backdrop community-navigator-backdrop--open"
              : "community-navigator-backdrop"
          }
          aria-hidden="true"
          onClick={() => {
            if (!drawerOpen) return;
            dismissDrawer();
          }}
        />
      ) : null}

      <aside
        id="community-navigator-sidebar"
        ref={sidebarRef}
        className={[
          "community-navigator-sidebar",
          drawerOpen ? "community-navigator-sidebar--open" : "",
          desktopCollapsed ? "community-navigator-sidebar--collapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        role={drawerIsModal ? "dialog" : undefined}
        aria-modal={drawerIsModal ? true : undefined}
        aria-labelledby={
          drawerIsModal ? "community-navigator-drawer-title" : undefined
        }
      >
        <button
          type="button"
          ref={closeButtonRef}
          className="community-navigator-drawer-close"
          aria-label="Close Community Navigator menu"
          onClick={dismissDrawer}
        >
          <CloseIcon />
        </button>

        <div className="community-navigator-sidebar-tools">
          <button
            type="button"
            className="community-navigator-sidebar-collapse"
            aria-label={
              sidebarCollapsed
                ? "Expand Community Navigator sidebar"
                : "Collapse Community Navigator sidebar"
            }
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            onClick={toggleSidebarCollapsed}
          >
            <span aria-hidden="true">{sidebarCollapsed ? "›" : "‹"}</span>
          </button>
        </div>

        <div className="community-navigator-brand">
          <img
            src="/logo-1.png"
            alt={desktopCollapsed ? "AcceptingMD" : ""}
            title={desktopCollapsed ? "AcceptingMD" : undefined}
            className="community-navigator-brand-logo"
          />
          <div className="community-navigator-brand-copy">
            <div className="community-navigator-brand-platform">AcceptingMD</div>
            <div
              id="community-navigator-drawer-title"
              className="community-navigator-brand-title"
            >
              Community Navigator
            </div>
            {ohtScopeLabel ? (
              <div className="community-navigator-brand-scope">{ohtScopeLabel}</div>
            ) : null}
          </div>
        </div>

        <nav className="community-navigator-nav" aria-label="Community Navigator">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive
                  ? "community-navigator-nav-link active"
                  : "community-navigator-nav-link"
              }
              title={
                desktopCollapsed
                  ? item.label === "Cases" && casesIntakeBadge
                    ? `Cases, ${casesIntakeBadge}`
                    : item.label
                  : undefined
              }
              onClick={(event) => handleGuardedNavClick(event, item.to)}
            >
              <CommunityNavigatorNavIcon label={item.label} />
              <span className="community-navigator-nav-label">{item.label}</span>
              {item.label === "Cases" && casesIntakeBadge ? (
                <span
                  className="community-navigator-nav-badge"
                  aria-label={`${casesIntakeBadge} cases with intake not started`}
                >
                  {casesIntakeBadge}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>

        <div
          className="community-navigator-sidebar-footer"
          aria-label="Community Navigator account actions"
        >
          {signedInEmail ? (
            <div className="community-navigator-signedin">
              <div className="community-navigator-signedin-label">
                Signed in as
              </div>
              <div className="community-navigator-signedin-email">
                {signedInEmail}
              </div>
            </div>
          ) : null}

          <div className="community-navigator-sidebar-actions">
            <NavLink
              to="/public"
              className={({ isActive }) =>
                isActive
                  ? "community-navigator-footer-link active"
                  : "community-navigator-footer-link"
              }
              title={desktopCollapsed ? "Public Site" : undefined}
              onClick={(event) => handleGuardedNavClick(event, "/public")}
            >
              <FooterActionIcon name="public" />
              <span className="community-navigator-footer-label">Public Site</span>
            </NavLink>

            {hasMultipleRoles ? (
              <NavLink
                to="/portal/select-role"
                className={({ isActive }) =>
                  isActive
                    ? "community-navigator-footer-link active"
                    : "community-navigator-footer-link"
                }
                title={desktopCollapsed ? "Switch Role" : undefined}
                onClick={(event) =>
                  handleGuardedNavClick(event, "/portal/select-role")
                }
              >
                <FooterActionIcon name="role" />
                <span className="community-navigator-footer-label">
                  Switch Role
                </span>
              </NavLink>
            ) : null}

            <a
              href={signOutHref}
              className="community-navigator-footer-link community-navigator-footer-link-primary"
              title={desktopCollapsed ? "Sign Out" : undefined}
              onClick={closeDrawer}
            >
              <FooterActionIcon name="signOut" />
              <span className="community-navigator-footer-label">Sign Out</span>
            </a>
          </div>
        </div>
      </aside>

      <main ref={mainRef} className="community-navigator-main">
        <Outlet />
      </main>
    </div>
  );
}